export function createPasswordId(prefix = 'pw') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function normalizeUrl(url) {
  if (!url) {
    return '';
  }

  try {
    return new URL(String(url).trim()).href;
  } catch (error) {
    return String(url).trim();
  }
}

export function normalizeOrigin(url) {
  const normalizedUrl = normalizeUrl(url);
  if (!normalizedUrl) {
    return '';
  }

  try {
    return new URL(normalizedUrl).origin;
  } catch (error) {
    return '';
  }
}

function deriveTitle(record) {
  const explicitTitle = String(record.title || record.name || '').trim();
  if (explicitTitle) {
    return explicitTitle;
  }

  const candidate = record.url || record.origin || '';
  try {
    return new URL(candidate).hostname || 'Untitled Password';
  } catch (error) {
    return candidate || 'Untitled Password';
  }
}

function normalizeTimestamp(value, fallbackValue) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  return fallbackValue;
}

export function normalizePasswordRecord(record = {}, options = {}) {
  const now = normalizeTimestamp(options.now, Date.now());
  const existing = options.existing || null;
  const url = normalizeUrl(record.url || record.origin || existing?.url || existing?.origin || '');
  const origin = normalizeOrigin(record.origin || url || existing?.origin || existing?.url || '');
  const createdAt = normalizeTimestamp(record.createdAt || existing?.createdAt, now);
  const updatedAt = normalizeTimestamp(record.updatedAt || record.capturedAt || existing?.updatedAt, now);
  const deletedAt = record.deletedAt
    ? normalizeTimestamp(record.deletedAt, updatedAt)
    : null;

  return {
    id: String(record.id || existing?.id || createPasswordId()),
    origin,
    url: url || existing?.url || origin,
    title: deriveTitle({ ...existing, ...record, origin, url }),
    username: String(record.username || existing?.username || '').trim(),
    password: String(record.password || existing?.password || ''),
    source: String(record.source || existing?.source || options.source || 'manual').trim() || 'manual',
    createdAt,
    updatedAt,
    deletedAt
  };
}

export function sortPasswordVault(entries = []) {
  return [...entries].sort((left, right) => {
    const leftDeleted = left.deletedAt ? 1 : 0;
    const rightDeleted = right.deletedAt ? 1 : 0;
    if (leftDeleted !== rightDeleted) {
      return leftDeleted - rightDeleted;
    }

    const leftUpdated = left.updatedAt || left.createdAt || 0;
    const rightUpdated = right.updatedAt || right.createdAt || 0;
    if (leftUpdated !== rightUpdated) {
      return rightUpdated - leftUpdated;
    }

    const leftKey = `${left.title || ''}${left.origin || ''}${left.username || ''}`.toLowerCase();
    const rightKey = `${right.title || ''}${right.origin || ''}${right.username || ''}`.toLowerCase();
    return leftKey.localeCompare(rightKey);
  });
}

export function normalizePasswordVault(entries = []) {
  return sortPasswordVault(entries.map(entry => normalizePasswordRecord(entry)));
}

export function getVisiblePasswordEntries(entries = []) {
  return sortPasswordVault(entries).filter(entry => !entry.deletedAt);
}

function findMatchingIndex(entries, entry) {
  const entryId = String(entry.id || '');
  if (entryId) {
    const byId = entries.findIndex(candidate => String(candidate.id || '') === entryId);
    if (byId >= 0) {
      return byId;
    }
  }

  const origin = String(entry.origin || normalizeOrigin(entry.url || ''));
  const username = String(entry.username || '').trim().toLowerCase();
  if (!origin && !username) {
    return -1;
  }

  return entries.findIndex(candidate => (
    String(candidate.origin || normalizeOrigin(candidate.url || '')) === origin &&
    String(candidate.username || '').trim().toLowerCase() === username
  ));
}

export function upsertPasswordEntry(entries = [], rawEntry = {}, options = {}) {
  const nextEntries = [...entries];
  const normalizedEntry = normalizePasswordRecord(rawEntry, options);
  const existingIndex = findMatchingIndex(nextEntries, normalizedEntry);

  if (existingIndex >= 0) {
    const existing = nextEntries[existingIndex];
    nextEntries[existingIndex] = normalizePasswordRecord(
      {
        ...existing,
        ...normalizedEntry,
        id: existing.id,
        createdAt: existing.createdAt
      },
      {
        ...options,
        existing,
        now: normalizedEntry.updatedAt
      }
    );
  } else {
    nextEntries.push(normalizedEntry);
  }

  return sortPasswordVault(nextEntries);
}

export function markPasswordDeleted(entries = [], passwordId, options = {}) {
  const now = normalizeTimestamp(options.now, Date.now());

  return sortPasswordVault(entries.map(entry => (
    String(entry.id) === String(passwordId)
      ? {
          ...entry,
          deletedAt: now,
          updatedAt: now
        }
      : entry
  )));
}

export function mergePasswordVaults(leftEntries = [], rightEntries = []) {
  const orderedEntries = [...leftEntries, ...rightEntries]
    .map(entry => normalizePasswordRecord(entry))
    .sort((left, right) => {
      const leftUpdated = left.updatedAt || left.createdAt || 0;
      const rightUpdated = right.updatedAt || right.createdAt || 0;
      return leftUpdated - rightUpdated;
    });

  return orderedEntries.reduce(
    (result, entry) => upsertPasswordEntry(result, entry, { now: entry.updatedAt }),
    []
  );
}

export function serializePasswordVault(entries = []) {
  return JSON.stringify(normalizePasswordVault(entries).map(entry => ({
    id: entry.id,
    origin: entry.origin,
    url: entry.url,
    title: entry.title,
    username: entry.username,
    password: entry.password,
    source: entry.source,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
    deletedAt: entry.deletedAt
  })));
}

export function passwordVaultsEqual(leftEntries = [], rightEntries = []) {
  return serializePasswordVault(leftEntries) === serializePasswordVault(rightEntries);
}

function parseCsvRows(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (inQuotes) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += character;
      }
      continue;
    }

    if (character === '"') {
      inQuotes = true;
      continue;
    }

    if (character === ',') {
      row.push(field);
      field = '';
      continue;
    }

    if (character === '\n' || character === '\r') {
      row.push(field);
      field = '';
      if (row.some(value => value !== '')) {
        rows.push(row);
      }
      row = [];

      if (character === '\r' && text[index + 1] === '\n') {
        index += 1;
      }
      continue;
    }

    field += character;
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    if (row.some(value => value !== '')) {
      rows.push(row);
    }
  }

  return rows;
}

function resolveHeaderIndexes(headerRow) {
  const normalizedHeaders = headerRow.map(header => String(header || '').trim().toLowerCase());
  const lookup = (aliases) => normalizedHeaders.findIndex(header => aliases.includes(header));

  const indexes = {
    url: lookup(['url', 'website', 'origin', 'login_url']),
    title: lookup(['name', 'title', 'site name']),
    username: lookup(['username', 'user name', 'login', 'email']),
    password: lookup(['password', 'pass'])
  };

  if (indexes.url === -1 || indexes.password === -1) {
    throw new Error('Invalid Chrome password export. Expected at least "url" and "password" columns.');
  }

  return indexes;
}

export function parseChromePasswordCsv(csvText) {
  const rows = parseCsvRows(String(csvText || ''));
  if (!rows.length) {
    throw new Error('The selected file is empty.');
  }

  const indexes = resolveHeaderIndexes(rows[0]);
  const entries = [];
  const invalidRows = [];

  rows.slice(1).forEach((row, rowIndex) => {
    const rawUrl = row[indexes.url] || '';
    const rawPassword = row[indexes.password] || '';
    const rawUsername = indexes.username >= 0 ? (row[indexes.username] || '') : '';
    const rawTitle = indexes.title >= 0 ? (row[indexes.title] || '') : '';

    if (![rawUrl, rawPassword, rawUsername, rawTitle].some(Boolean)) {
      return;
    }

    if (!String(rawUrl).trim() || !String(rawPassword).trim()) {
      invalidRows.push({
        rowNumber: rowIndex + 2,
        reason: 'Missing required url or password value.'
      });
      return;
    }

    entries.push(normalizePasswordRecord({
      url: rawUrl,
      title: rawTitle,
      username: rawUsername,
      password: rawPassword,
      source: 'imported',
      updatedAt: Date.now()
    }));
  });

  return {
    entries: sortPasswordVault(entries),
    invalidRows
  };
}

export function buildPasswordImportPreview(existingEntries = [], importedEntries = []) {
  let workingVault = normalizePasswordVault(existingEntries);
  let additions = 0;
  let updates = 0;

  importedEntries.forEach(entry => {
    const existingIndex = findMatchingIndex(workingVault, entry);
    if (existingIndex >= 0) {
      updates += 1;
    } else {
      additions += 1;
    }
    workingVault = upsertPasswordEntry(workingVault, entry, { source: 'imported' });
  });

  return {
    mergedEntries: workingVault,
    additions,
    updates,
    importedCount: importedEntries.length
  };
}
